import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaEnvelope,
  FaPhoneAlt,
  FaShieldAlt,
  FaFileAlt,
  FaMapMarkedAlt,
  FaPlaneDeparture,
} from 'react-icons/fa';

import { Headercomponent } from '../components/headercomponent';
import { Footer } from '../components/Footer';
import NodeBackground from '../components/nodeBackground';

const contactOptions = [
  {
    title: 'Request a Consultation',
    text: 'Use this for travel, residential, executive exposure, event, or protective intelligence questions.',
    action: 'Email Weathervane',
    href: 'mailto:info@weathervaneintel.com?subject=Weathervane%20Intel%20Consultation%20Request',
    icon: <FaEnvelope />,
  },
  {
    title: 'Discuss an Intelligence Product',
    text: 'Ask about representative work, report scope, pricing, timelines, or a location-specific assessment.',
    action: 'View Intelligence Products',
    to: '/intelligence_products',
    icon: <FaFileAlt />,
  },
  {
    title: 'Platform or Client Access',
    text: 'For platform questions, current client access, or threat monitoring workflows.',
    action: 'Client Login',
    href: 'https://app.weathervaneintel.com',
    icon: <FaShieldAlt />,
  },
];

const inquiryTypes = [
  {
    title: 'Travel or Hotel Review',
    text: 'Send the destination, hotel, venue, dates, and any known concerns.',
    icon: <FaPlaneDeparture />,
  },
  {
    title: 'Property or Neighborhood Assessment',
    text: 'Send the address, city, decision timeline, and whether the review is for purchase, lease, or relocation.',
    icon: <FaMapMarkedAlt />,
  },
  {
    title: 'Executive Exposure or OSINT Concern',
    text: 'Send the person, organization, public concern, or intelligence question that needs review.',
    icon: <FaShieldAlt />,
  },
];

function Contact() {
  return (
    <>
      <Headercomponent />

      <div className="container">
        <div className="hero">
          <NodeBackground />

          <div className="hero-content">
            <h1>Contact Weathervane Intel</h1>
            <p>
              For intelligence products, crime and safety reports, travel risk reviews, executive exposure
              assessments, event support, or platform inquiries, contact Weathervane directly to scope the
              request.
            </p>

            <div className="hero-actions">
              <a
                className="cta-button"
                href="mailto:info@weathervaneintel.com?subject=Weathervane%20Intel%20Inquiry"
              >
                Email Weathervane
              </a>
              <a className="cta-button" href="tel:+1(215)-618-4273">
                Call Weathervane Intel
              </a>
            </div>

            <div className="hero-proof-row">
              <span>Private assessments</span>
              <span>Protective intelligence</span>
              <span>Crime mapping</span>
              <span>OSINT research</span>
            </div>
          </div>

          <div className="hero-diagram">
            <div className="service-graphic">
              <div className="service-graphic-header">
                <h3>Best Way to Start</h3>
              </div>

              <div className="service-graphic-grid">
                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span className="service-graphic-icon"><FaEnvelope /></span>
                    <span>Email the intelligence question</span>
                  </div>
                  <span className="service-arrow">01</span>
                </div>

                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span className="service-graphic-icon"><FaFileAlt /></span>
                    <span>Share the location, person, event, or concern</span>
                  </div>
                  <span className="service-arrow">02</span>
                </div>

                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span className="service-graphic-icon"><FaShieldAlt /></span>
                    <span>We scope the right assessment or product</span>
                  </div>
                  <span className="service-arrow">03</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="platform-overview">
          <h2>How to Get in Contact</h2>
          <p>
            The fastest way to start is to email the intelligence question or decision you are trying to
            support. Include the relevant location, person, organization, event, or travel details, and
            Weathervane can recommend the appropriate product, report, or monitoring approach.
          </p>

          <div className="overview-cards">
            {contactOptions.map((option) => (
              <article className="overview-card" key={option.title}>
                <div className="service-graphic-icon" style={{ fontSize: '1.35rem', marginBottom: '1rem' }}>
                  {option.icon}
                </div>
                <h3>{option.title}</h3>
                <p>{option.text}</p>
                {option.to ? (
                  <Link className="service-detail-button" to={option.to}>
                    {option.action}
                  </Link>
                ) : (
                  <a
                    className="service-detail-button"
                    href={option.href}
                    target={option.href?.startsWith('http') ? '_blank' : undefined}
                    rel={option.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {option.action}
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="process-deliverables-section">
          <div className="process-column">
            <span className="section-kicker">What to Include</span>
            <h2>Send enough detail to scope the request.</h2>
            <p>
              You do not need a perfect scope before reaching out. A short description of the decision,
              concern, or location is enough to begin a conversation.
            </p>

            <div className="process-steps">
              {inquiryTypes.map((item, index) => (
                <div className="process-step" key={item.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="deliverables-column">
            <span className="section-kicker">Direct Contact</span>
            <h2>Email</h2>
            <p>
              For consultations, representative work, report requests, or platform questions, use the
              email below.
            </p>

            <div className="deliverables-grid">
              <div className="deliverable-card">
                <h3>Email</h3>
                <p>
                  <a href="mailto:info@weathervaneintel.com" className="text-link">
                    info@weathervaneintel.com
                  </a>
                </p>
              </div>

              <div className="deliverable-card">
                <h3>Typical Requests</h3>
                <p>Travel risk, residential crime reports, executive exposure, event support, and OSINT questions.</p>
              </div>

              <div className="deliverable-card">
                <h3>Best First Step</h3>
                <p>Send the location, person, event, or concern and the decision you need to support.</p>
              </div>

              <div className="deliverable-card">
                <h3>Client Access</h3>
                <p>
                  <a
                    href="https://app.weathervaneintel.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                  >
                    Open the client platform
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Need an intelligence answer before a decision?</h2>
          <p>
            Contact Weathervane Intel to discuss the location, person, event, or concern that needs review.
          </p>
          <a
            className="cta-button"
            href="mailto:info@weathervaneintel.com?subject=Weathervane%20Intel%20Assessment%20Request"
          >
            Start the Conversation
          </a>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default Contact;
